define('home/views/DiscussionPageView',[
    'jquery',
    'underscore',

    'core/bus',
    'core/utils/storage',

    'home/utils/backboneUtils',
    'home/views/common/TabbedView',
    'home/views/NetworkDiscussionCoverView',
    'home/views/UserGeneratedDiscussionCoverView',
    'home/views/EmbedContainerView',
    'home/views/LoungeView',
    'home/views/FetchingView',

    'home/templates/deletedThreadError',
    'home/templates/discussionPage',

    'home/mixins/withClickLinkTracking',
], function (
    $,
    _,

    bus,
    storage,

    backboneUtils,
    TabbedView,
    NetworkDiscussionCoverView,
    UserGeneratedDiscussionCoverView,
    EmbedContainerView,
    LoungeView,
    FetchingView,

    deletedThreadErrorTemplate,
    discussionPageTemplate,

    withClickLinkTracking
) {
    'use strict';

    var parent = TabbedView;
    var parentProto = parent.prototype;

    /**
     * A page for reading a thread, either a network thread or a user-generated thread.
     * View expects to be backed by a DiscussionViewModel and the router instance in options.app.
     */
    var DiscussionPageView = parent.extend({
        defaultTab: function () {
            var userDefault = storage.get('disqus.sort');
            var defaultTab;

            if (userDefault) {
                defaultTab = _.findKey(DiscussionPageView.TAB_SORTS, function (val) {
                    return val === userDefault;
                });
            }

            return defaultTab || 'best';
        },

        saveDefaultTab: function (activeTab) {
            storage.set(
                'disqus.sort',
                DiscussionPageView.TAB_SORTS[activeTab] || DiscussionPageView.TAB_SORTS.best
            );
        },

        template: function (data) {
            return data.thread.isDeleted ? deletedThreadErrorTemplate(data) : discussionPageTemplate(data);
        },

        regions: _.defaults({
            cover: '[data-role=discussion-cover]',
            contentRegion: '#discussion-comments',
            relatedRegion: '[data-role=related]',
        }, parentProto.regions),

        initialize: function (options) {
            parentProto.initialize.call(this, options);

            this.app = options.app;

            this.listenToOnce(this.cover, 'show', function () {
                bus.trigger('showDiscussionCover', 'discussion');
            });

            this.listenTo(this, 'change:activeTab', this.saveDefaultTab);
            this.listenTo(this.model.thread, 'sync', this.render);

            this.noEmbed = options.noEmbed;
        },

        events: _.defaults({
            'click a[data-link-name]': 'trackClickLink',
            'click button[data-action=scroll-top]': 'scrollToTop',
        }, parentProto.events),

        onDomRefresh: function () {
            parentProto.onDomRefresh.call(this);

            this.showCover();
        },

        showCover: function () {
            // Until thread is fetched, we can't be sure if it user-generated or network.
            // Show a fetching view until we can determine which cover view to show.
            if (this.model.thread.shouldFetch()) {
                this.cover.show(new FetchingView());
                return;
            }

            var CoverView = this.model.thread.get('isUserGenerated') ?
                UserGeneratedDiscussionCoverView :
                NetworkDiscussionCoverView;

            var coverView = new CoverView({
                model: this.model,
                app: this.app,
            });

            // if cover re-renders, we need to show the correct tab as active
            coverView.listenTo(coverView, 'dom:refresh', _.bind(this.updateTabClasses, this));

            this.cover.show(coverView);
        },

        getPageTitle: function () {
            var thread = this.model.thread;
            return $.when(
                backboneUtils.getPromiseFor(thread, 'title'),
                backboneUtils.getPromiseFor(thread.forum, 'name')
            ).then(function (threadTitle, forumName) {
                return threadTitle + ' · ' + forumName;
            });
        },

        getMetaAttrs: function () {
            var thread = this.model.thread;
            return this.getPageTitle().then(function (pageTitle) {
                var image = thread.getUserGeneratedImage();
                return {
                    title: pageTitle,

                    // `mettags.set` method takes care of stripping and
                    // cleaning a chunk of HTML to turn it into a description.
                    description: thread.get('message'),
                    image: image && image.src,
                    type: 'article',
                };
            });
        },

        getChannelSlug: function () {
            var channel = this.model.channel;

            // If channel has already been fetched or id was given in the
            // url, can return immediately.
            if (channel)
                return channel.get('slug');

            // If the thread is fully fetched and there's no channel, this
            // is a network thread and doesn't have a channel. Return null.
            if (!this.model.thread.shouldFetch())
                return null;

            // Fetch the thread and check once that's done for the channel.
            var deferred = $.Deferred();
            this.model.thread.fetch().then(_.bind(function () {
                var channel = this.model.channel;
                deferred.resolve(channel ? channel.get('slug') : null);
            }, this));

            return deferred.promise();
        },

        getPageTrackingParams: function () {
            // Guaranteed to have forum id here from the url
            var params = {
                forum: this.model.thread.forum.id,
            };

            return $.when(this.getChannelSlug(), this.getPageTitle())
                .then(_.bind(function (channelSlug, pageTitle) {
                    if (channelSlug)
                        params.channel_slug = channelSlug;

                    if (pageTitle)
                        params.title = pageTitle;

                    // Channel discussion pages (with the channel in the url)
                    // fetch the author along with the thread. We can use that
                    // author info in tracking. Non-channel discussion pages
                    // for user generated threads will have the author created
                    // but not yet fetched at this point. Don't bother waiting,
                    // we don't link to such pages anyway (though they do work)
                    var author = this.model.thread.author;
                    var authorUsername = author && author.get('username');
                    if (authorUsername)
                        params.author = authorUsername;

                    return params;
                }, this));
        },

        getTrackingSection: function (activeTab) {
            return activeTab;
        },

        getTabView: function (activeTab) {
            var ContentView = this.noEmbed ? LoungeView : EmbedContainerView;
            var options = {
                model: this.model.thread,
                sortOrder: DiscussionPageView.TAB_SORTS[activeTab] || 'popular',
            };

            return new ContentView(options);
        },

        scrollToTop: function () {
            window.scrollTo(0, 0);
        },
    }, {
        TAB_SORTS: {
            'best': 'popular',
            'newest': 'desc',
            'oldest': 'asc',
        },
    });

    withClickLinkTracking.call(DiscussionPageView.prototype);

    return DiscussionPageView;
});

